import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { NgForOf, NgIf } from '@angular/common';
import { KeyComboButtonComponent } from '../../atoms/key-combo-button/key-combo-button.component';
import { GlobalSearchStore } from '../../../stores/global-search.state';
import { MenuItem } from 'primeng/api';
import { TabMenuModule } from 'primeng/tabmenu';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { TableModule } from 'primeng/table';
import { GlobalsStore } from '../../../stores/globals.state';
import { TableRowIndicatorComponent } from '../../atoms/table-row-indicator/table-row-indicator.component';
import { TableRowDataLayerTypeComponent } from '../../atoms/table-row-data-layer-type/table-row-data-layer-type.component';
import { TableRowBookmarkComponent } from '../../atoms/table-row-bookmark/table-row-bookmark.component';
import { LocalStorageService } from '../../../services/local-storage.service';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { TableRowTopicComponent } from '../../atoms/table-row-topic/table-row-topic.component';
import { TableRowStoryComponent } from '../../atoms/table-row-story/table-row-story.component';
import { TableRowColorsetComponent } from '../../atoms/table-row-colorset/table-row-colorset.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagFilterDropdownComponent } from '../../atoms/tag-filter-dropdown/tag-filter-dropdown.component';
import { TreeSelectModule } from 'primeng/treeselect';
import { FormsModule } from '@angular/forms';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { Category } from '../../../models/category';
import { CategoryStore } from '../../../stores/category/category.state';
import { IndicatorTreeHelperService } from '../../../indicator-tree-helper.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MaskComponent } from '../mask/mask.component';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    SearchInputComponent,
    DialogModule,
    ButtonModule,
    NgIf,
    KeyComboButtonComponent,
    TabMenuModule,
    RippleModule,
    BadgeModule,
    TableModule,
    TableRowIndicatorComponent,
    TableRowDataLayerTypeComponent,
    TableRowBookmarkComponent,
    TableRowStoryComponent,
    TableRowColorsetComponent,
    TableRowTopicComponent,
    NgForOf,
    AlertComponent,
    IconFieldModule,
    InputIconModule,
    TagFilterDropdownComponent,
    TreeSelectModule,
    FormsModule,
    MaskComponent,
  ],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  readonly userStore = inject(ActiveUserStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly globalSearchStore = inject(GlobalSearchStore);
  readonly globalStore = inject(GlobalsStore);
  visible: any;
  active = 'indicators';
  queryString = '';
  queryTags: string[] = [];
  queryCategory: Category | null = null;
  searchValues: string[] = [];
  results?: { [key: string]: any[] };
  tree: any;

  constructor(
    private localStorageService: LocalStorageService,
    private treeHelper: IndicatorTreeHelperService
  ) {}

  tabs: MenuItem[] = [
    {
      visible: this.userStore.canAccessIndicators(),
      label: 'Indikatoren',
      key: 'indicators',
      icon: 'fas fa-ruler-vertical',
      command: event => {
        this.active = 'indicators';
      },
    },
    {
      visible: true,
      label: 'Räume/ Standorte',
      key: 'dataLayerTypes',
      icon: 'fas fa-map-location-dot',
      command: event => {
        this.active = 'dataLayerTypes';
      },
    },
    {
      visible: this.userStore.canAccessBookmarks(),
      label: 'Ansichten',
      key: 'bookmarks',
      icon: 'fas fa-note-sticky',
      command: event => {
        this.active = 'bookmarks';
      },
    },
    {
      visible: this.userStore.canAccessStories(),
      label: 'Berichte',
      key: 'stories',
      icon: 'fas fa-file-lines',
      command: event => {
        this.active = 'stories';
      },
    },
    {
      visible: this.userStore.canAccessColorsets(),
      label: 'Farbsets',
      key: 'colorsets',
      icon: 'fas fa-palette',
      command: event => {
        this.active = 'colorsets';
      },
    },
    {
      visible: this.userStore.canAccessTopics(),
      label: 'Themen',
      key: 'topics',
      icon: 'fas fa-book-open',
      command: event => {
        this.active = 'topics';
      },
    },
  ];

  activeTab: MenuItem = this.tabs.filter(tab => tab.visible)[0];

  /**
   * Handles the change of the active item in the menu.
   *
   * @param {MenuItem} event - The menu item that triggered the change.
   * @return {void} Does not return any value.
   */
  onActiveItemChange(event: MenuItem) {
    this.activeTab = event;
    this.active = this.activeTab['key'];
  }

  /**
   * Initializes the component by retrieving search values from the local storage service
   * and assigns them to the searchValues property.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.resetQuery();
    this.searchValues = this.localStorageService.getSearchValues();
    const groupedIndicators = this.indicatorStore.groupedByCategory();
    const initialCategoryTree = this.categoryStore.asTree();
    if (initialCategoryTree && initialCategoryTree.length) {
      setTimeout(() => {
        this.tree = this.treeHelper.filterCategoriesWithIndicators(
          initialCategoryTree,
          groupedIndicators
        );
      });
    }
  }

  /**
   * Updates the query string with the provided text and triggers a search operation.
   *
   * @param {string} text - The new query string to update.
   * @return {void}
   */
  updateQueryString(text: string): void {
    this.queryString = text;
    this.search();
  }

  /**
   * Updates the queryTags property with the provided tags array and triggers a search operation.
   *
   * @param {string[]} [tags] - An optional array of tags to update. If not provided, the queryTags will be set to an empty array.
   * @return {void}
   */
  updateQueryTags(tags?: string[]): void {
    this.queryTags = tags || [];
    this.search();
  }

  /**
   * Handles the category search functionality by updating the filter with the selected category
   * and triggering the search operation.
   *
   * @param {any} event - The event object containing information about the selected category,
   *                      specifically the node key representing the category.
   * @return {void} This method does not return a value.
   */
  callCategorySearch(event?: any): void {
    if (event) {
      this.indicatorStore.updateFilter({
        category: this.categoryStore
          .fetchCategoryChildren(event.node.key)
          .map(item => item._id)
          .concat(event.node.key),
      });
      this.onActiveItemChange(this.tabs[0]);
    } else {
      this.indicatorStore.updateFilter({ category: [] });
    }

    queueMicrotask(() => this.search());
  }

  /**
   * Resets the current query state by clearing the query string and query tags.
   * Also triggers a new search with the cleared query state.
   *
   * @return {void} Does not return any value.
   */
  resetQuery(): void {
    this.queryString = '';
    this.queryTags = [];
    this.queryCategory = null;
    this.indicatorStore.updateFilter({ category: [] });

    this.search();
  }

  search(): void {
    this.globalSearchStore.updateQuery(this.queryString, this.queryTags, false);
    if (!this.queryString && !this.queryTags.length) {
      this.results = this.globalSearchStore.filteredResults();
    } else {
      this.results = this.globalSearchStore.filteredResults();
    }

    //this.globalSearchStore.updateQuery(query);
    ////this.results = this.globalSearchStore.filteredResults();
    ////if (this.results) {
    ////  //this.localStorageService.addSearchValue(query);
    ////}
    ////this.searchValues = this.localStorageService.getSearchValues();
  }

  /**
   * Performs a quick search based on the provided query string.
   *
   * @param {string} query - The search term used to execute the quick search.
   * @return {void} This method does not return anything.
   */
  quickSearch(query: string): void {
    //this.search(query);
  }

  /**
   * Closes the current instance by resetting its state.
   * Sets `results` to undefined and makes the instance non-visible.
   *
   * @return {void} Returns nothing. This method alters the state of the instance.
   */
  close(): void {
    this.results = undefined;
    this.visible = false;
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * Performs necessary cleanup such as resetting the query state.
   *
   * @return {void} Does not return any value.
   */
  ngOnDestroy(): void {
    this.resetQuery();
    this.close();
  }
}
